<template>
    <card card-classes="mb-0">
        <template #header>
            <h3 class="mb-0 truncate">{{ title }}</h3>
            <div
                v-c-tooltip="{
                    trigger: disabled ? 'hover' : '',
                    content: disabled ? trans('asset.action_disabled_due_to_incomplete_onboarding') : '',
                }"
            >
                <modal-link
                    v-if="createUrl && hasAnyPermission(['quality_assurance.create'])"
                    :disabled="disabled"
                    :href="createUrl"
                    class="btn btn-secondary min-w-0 text-nowrap"
                    ><span class="flex items-center gap-1 truncate"
                        ><span>{{ trans('general.action.add') }}</span
                        ><span style="color: rgb(228, 229, 230)">|</span><icon name="cil-pencil" /></span
                ></modal-link>
            </div>
        </template>

        <div class="items d-flex flex-column gap-3">
            <div v-if="props.items.length === 0">
                <p class="mb-0" style="font-size: 0.875rem; line-height: 1.125rem; font-weight: 500">
                    {{ emptyText }}
                </p>
            </div>

            <quality-assurance-configuration-item
                v-for="item in props.items"
                :id="item.id"
                :key="item.id"
                :configName="item.configName"
                :editable="item.isEditable && !disabled"
                :is-loading="itemsLoading.includes(item.id)"
                :is-paused="item.isPaused"
                :output-type="item.outputType"
                :recurrence="item.recurrence"
                :repeats="getHumanReadableText(item)"
                :scheduled-item="item.scheduledItem"
                :start-date="item.startDate"
                :timezone="item.timezone"
                :service-level-agreement="item.serviceLevelAgreement"
                :title="item.title"
                @delete="handleDelete"
                @edit="handleEdit"
            />
        </div>
    </card>

    <delete-sla-schedule-modal
        :is-processing="isDeleteProcessing"
        :show="pendingDeleteItem !== null"
        :sla="pendingDeleteItem?.serviceLevelAgreement"
        @close="pendingDeleteItem = null"
        @confirm="confirmSlaDelete"
    />
</template>

<script setup>
import Card from '@/Components/Card.vue';
import { computed, ref } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { recurrenceToHumanReadableText } from '@/Utils/recurrenceToHumanReadableText';
import ModalLink from '@/Components/ModalLink.vue';
import QualityAssuranceConfigurationItem from './QualityAssuranceConfigurationItem.vue';
import DeleteSlaScheduleModal from './DeleteSlaScheduleModal.vue';
import { visitModal } from '@/Utils/visitModal';

const page = usePage();
const currentLocale = computed(() => page.props.locale);

const props = defineProps({
    title: String,
    emptyText: String,
    createUrl: String,
    disabled: {
        type: Boolean,
        default: false,
    },
    items: {
        type: Array,
        default: () => [],
    },
    assetId: {
        type: String,
        required: true,
    },
});

const itemsLoading = ref([]);
const items = computed(() => page.props.qualityAssuranceConfigurations[props.type]?.data || []);
const pendingDeleteItem = ref(null);
const isDeleteProcessing = computed(() => pendingDeleteItem.value !== null && itemsLoading.value.includes(pendingDeleteItem.value.id));

const handleEdit = (id) => {
    itemsLoading.value.push(id);

    visitModal(route('quality-assurance.configurations.edit', { configuration: id, asset_id: props.assetId })).finally(() => {
        itemsLoading.value.splice(itemsLoading.value.indexOf(id), 1);
    });
};

const handleDelete = (id) => {
    const item = props.items.find((entry) => entry.id === id);

    // SLA-linked schedules are shared across the covered fleet, so deleting one
    // removes it for every covered asset. Confirm the blast radius first.
    if (item?.serviceLevelAgreement) {
        pendingDeleteItem.value = item;

        return;
    }

    performDelete(id);
};

const performDelete = (id) => {
    itemsLoading.value.push(id);

    router.delete(route('quality-assurance.configurations.destroy', { configuration: id, asset_id: props.assetId }), {
        preserveState: true,
        preserveScroll: true,
        onFinish: () => {
            itemsLoading.value = itemsLoading.value.filter((loadingId) => loadingId !== id);
            pendingDeleteItem.value = null;
        },
    });
};

const confirmSlaDelete = () => {
    if (pendingDeleteItem.value) {
        performDelete(pendingDeleteItem.value.id);
    }
};

const getHumanReadableText = (item) =>
    recurrenceToHumanReadableText({ ...item.recurrence, startDate: item.startDate, endDate: item.endDate }, currentLocale.value);
</script>
